import {
  Card,
  Title,
  Subtitle,
  BarChart,
  LineChart,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
} from "@tremor/react";
import { Scissors } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TableScroll } from "@/components/ui/TableScroll";
import { KpiCard } from "@/components/ui/KpiCard";
import { deriveWaste } from "@/data/derived";
import { useFilterVersion } from "@/data/filters";
import { hasPierderi } from "@/data/dataSource";
import { eur, eurAxis, procent, numar, formatLunaKey } from "@/lib/format";

export function PierderiSection() {
  useFilterVersion();
  const w = deriveWaste();
  const disponibil = hasPierderi();

  const chartOperatii = w.peOperatie.map((o) => ({
    operatie: o.operatie,
    "Cost pierdere (€)": Math.round(o.cost_pierdere_eur),
  }));
  const chartTrend = w.peLuna.map((l) => ({
    luna: formatLunaKey(l.luna),
    "Pierdere %": l.pierdere_pct,
  }));

  return (
    <section>
      <SectionHeader
        id="pierderi"
        icon={Scissors}
        titlu="8. Pierdere / waste pe operații"
        descriere="Rebut pe traseul Optima (tăiere, șlefuire, laminare, călire…) — unde se duce materialul și cât costă."
      />

      {!disponibil && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
          Set „Pierderi operații" neîncărcat — se afișează datele demo. Importă-l din panoul de import
          (template CSV disponibil) pentru cifrele reale Optima.
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <KpiCard
          accent="rose"
          eticheta="Cost total pierderi (perioadă)"
          valoare={eur(w.totalCost, 0)}
          subtext={`${numar(w.totalPierdereM2, 0)} m² rebut`}
        />
        <KpiCard
          accent="amber"
          eticheta="Pierdere medie /operație"
          valoare={procent(w.pctMediu)}
          subtext="raport pierdere / intrare material"
        />
        <KpiCard
          accent="blue"
          eticheta="Operație cu cea mai mare pierdere"
          valoare={w.peOperatie[0]?.operatie ?? "—"}
          subtext={w.peOperatie[0] ? `${eur(w.peOperatie[0].cost_pierdere_eur, 0)} · ${procent(w.peOperatie[0].pierdere_pct)}` : ""}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="ring-slate-200">
          <Title>Cost pierdere pe operație</Title>
          <Subtitle>Unde se pierde cel mai mult material (valorizat)</Subtitle>
          <BarChart
            className="mt-4 h-72 w-full"
            data={chartOperatii}
            index="operatie"
            categories={["Cost pierdere (€)"]}
            colors={["rose"]}
            layout="vertical"
            valueFormatter={eurAxis}
            showLegend={false}
            yAxisWidth={132}
            noDataText="Fără date"
          />
        </Card>
        <Card className="ring-slate-200">
          <Title>Evoluție pierdere % în timp</Title>
          <Subtitle>Trend lunar al ratei de rebut pe total traseu</Subtitle>
          <LineChart
            className="mt-4 h-72 w-full"
            data={chartTrend}
            index="luna"
            categories={["Pierdere %"]}
            colors={["rose"]}
            valueFormatter={(v) => `${numar(v, 1)}%`}
            showLegend={false}
            curveType="monotone"
            yAxisWidth={44}
            noDataText="Fără date"
          />
        </Card>
      </div>

      <Card className="mt-4 ring-slate-200">
        <Title>Detaliu pe operație</Title>
        <Subtitle>Material intrat vs. rebut și cost asociat</Subtitle>
        <TableScroll minWidth="48rem">
          <Table className="mt-3">
            <TableHead>
              <TableRow className="border-slate-200">
                <TableHeaderCell>Operație</TableHeaderCell>
                <TableHeaderCell className="text-right">Intrare (m²)</TableHeaderCell>
                <TableHeaderCell className="text-right">Pierdere (m²)</TableHeaderCell>
                <TableHeaderCell className="text-right">Pierdere %</TableHeaderCell>
                <TableHeaderCell className="text-right">Cost pierdere</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {w.peOperatie.map((o) => (
                <TableRow key={o.operatie} className="border-slate-100">
                  <TableCell className="font-medium text-slate-800">{o.operatie}</TableCell>
                  <TableCell className="text-right tabular text-slate-600">{numar(o.intrare_m2, 0)}</TableCell>
                  <TableCell className="text-right tabular text-slate-600">{numar(o.pierdere_m2, 1)}</TableCell>
                  <TableCell className="text-right">
                    <Badge size="xs" color={o.pierdere_pct >= 6 ? "rose" : o.pierdere_pct >= 3 ? "amber" : "emerald"}>
                      {procent(o.pierdere_pct)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular font-medium text-slate-800">{eur(o.cost_pierdere_eur, 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      </Card>
    </section>
  );
}
